import type { ReactNode } from "react"
import { Handle, Position, useConnection, useStore } from "@xyflow/react"
import { cn } from 'utils/classnames'
import type { BaseNodeComponentData } from "../../types"
import { DEFAULT_NODE_SIZE } from "../../constants"
import styles from "./base-node.module.scss"

interface NodeHandlesProps {
    interactive?: boolean
    isConnectable?: boolean
}

export function NodeHandles({ interactive = true, isConnectable = true }: NodeHandlesProps) {
    const connection = useConnection()
    const isConnecting = connection.inProgress && interactive && isConnectable
    const baseClass = !interactive && styles.handleDisabled
    const sourceClass = cn(styles.handle, baseClass, isConnecting && styles.handleConnecting)
    const targetClass = cn(styles.handle, baseClass, isConnecting && styles.handleConnectingTarget)

    return (
        <>
            <Handle type="target" position={Position.Top} id="top-target" className={targetClass} isConnectable={isConnectable} />
            <Handle type="source" position={Position.Top} id="top-source" className={sourceClass} isConnectable={isConnectable} />

            <Handle type="target" position={Position.Bottom} id="bottom-target" className={targetClass} isConnectable={isConnectable} />
            <Handle type="source" position={Position.Bottom} id="bottom-source" className={sourceClass} isConnectable={isConnectable} />

            <Handle type="target" position={Position.Left} id="left-target" className={targetClass} isConnectable={isConnectable} />
            <Handle type="source" position={Position.Left} id="left-source" className={sourceClass} isConnectable={isConnectable} />

            <Handle type="target" position={Position.Right} id="right-target" className={targetClass} isConnectable={isConnectable} />
            <Handle type="source" position={Position.Right} id="right-source" className={sourceClass} isConnectable={isConnectable} />
        </>
    )
}

export interface BaseNodeProps {
    selected?: boolean
    nodeData: BaseNodeComponentData
    className?: string
    disabledClassName?: string
    labelClassName?: string
    selectedClassName?: string
    nodeSize?: number
    isConnectable?: boolean
    children: ReactNode
}

export function BaseNode({
    selected,
    nodeData,
    className,
    disabledClassName,
    labelClassName,
    selectedClassName,
    nodeSize = DEFAULT_NODE_SIZE,
    isConnectable,
    children
}: BaseNodeProps) {
    const { displayName, disabled, readonly } = nodeData
    const nodesConnectable = useStore((state) => state.nodesConnectable)

    return (
        <div className={styles.nodeContainer}>
            <div className={styles.nodeWrapper} style={{ width: nodeSize, height: nodeSize }}>
                {selected && (
                    <div className={cn(styles.selectionRing, selectedClassName)} />
                )}
                <div className={cn(
                    styles.nodeContent,
                    className,
                    disabled && disabledClassName,
                    { [styles.readonly]: readonly }
                )}>
                    {children}
                </div>
                <NodeHandles interactive={nodesConnectable} isConnectable={isConnectable} />
            </div>
            <span className={cn(
                styles.nodeLabel,
                labelClassName,
                readonly && styles.labelReadonly,
                disabled && styles.labelDisabled
            )}>
                {displayName}
            </span>
        </div>
    )
}
